import { registerAuditLabels } from "@vixio/ui";
import type { StatusMap } from "@vixio/ui";

/** Tipos das linhas e RPCs do módulo surveys (ver modules/surveys/README.md). */

export type ProjectStatus = "planning" | "fieldwork" | "closed";
export type ResponseStatus = "pending" | "flagged" | "approved" | "rejected";
export type SampleMethod = "proportion" | "mean" | "stratified" | "quota" | "census";
export type InterviewerStatus = "candidate" | "approved" | "rejected" | "inactive";

export const PROJECT_STATUS: StatusMap = {
  planning: { label: "planejamento", tone: "neutral" },
  fieldwork: { label: "em campo", tone: "info" },
  closed: { label: "encerrado", tone: "ok" },
};

export const RESPONSE_STATUS: StatusMap = {
  pending: { label: "pendente", tone: "neutral" },
  flagged: { label: "sinalizada", tone: "warn" },
  approved: { label: "aprovada", tone: "ok" },
  rejected: { label: "descartada", tone: "crit" },
};

export const INTERVIEWER_STATUS: StatusMap = {
  candidate: { label: "candidato", tone: "info" },
  approved: { label: "aprovado", tone: "ok" },
  rejected: { label: "recusado", tone: "crit" },
  inactive: { label: "inativo", tone: "neutral" },
};

export const METHOD_LABEL: Record<SampleMethod, string> = {
  proportion: "Proporção (Cochran)",
  mean: "Média",
  stratified: "Estratificada proporcional",
  quota: "Cotas",
  census: "Censo",
};

export const FLAG_LABEL: Record<string, string> = {
  too_fast: "rápida demais",
  too_slow: "lenta demais",
  out_of_hours: "fora do horário",
  missing_geo: "sem localização",
  straightlining: "respostas iguais",
  quota_full: "cota cheia",
};

export const MODE_LABEL: Record<string, string> = { kiosk: "Quiosque", interviewer: "Entrevistador" };

export interface Project {
  id: string;
  org_id: string;
  code: string;
  title: string;
  description: string | null;
  client_name: string | null;
  client_ref: string | null;
  status: ProjectStatus;
  form_slug: string | null;
  form_version_id: string | null;
  questionnaire: Questionnaire | null;
  timezone: string;
  min_duration_seconds: number;
  max_duration_seconds: number;
  allowed_hour_start: number;
  allowed_hour_end: number;
  require_geo: boolean;
  straightline_min_items: number;
  min_group_size: number;
  fieldwork_started_at: string | null;
  fieldwork_closed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface QItem {
  id: string;
  type: string;
  text: string;
  options?: Array<string | { value: string; label?: string }>;
}
export interface Questionnaire {
  title?: string;
  items: QItem[];
}

export interface SamplePlan {
  id: string;
  project_id: string;
  version: number;
  method: SampleMethod;
  params: Record<string, unknown>;
  sample_size: number;
  note: string | null;
  created_at: string;
}

export interface QuotaProgress {
  id: string;
  project_id: string;
  item_id: string;
  option_value: string;
  label: string;
  target: number;
  collected: number;
  approved: number;
  remaining: number;
}

export interface Interviewer {
  id: string;
  org_id: string;
  person_id: string;
  user_id: string | null;
  code: string | null;
  status: InterviewerStatus;
  city: string | null;
  availability: string | null;
  experience: string | null;
  privacy_ack_at: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  created_at: string;
}

export interface AssignmentProgress {
  id: string;
  project_id: string;
  interviewer_id: string;
  interviewer_code: string | null;
  interviewer_name: string;
  target: number;
  area: string | null;
  active: boolean;
  collected: number;
  approved: number;
  rejected: number;
  flagged: number;
  avg_duration_seconds: number | null;
}

export interface Kiosk {
  id: string;
  project_id: string;
  label: string;
  active: boolean;
  created_at: string;
}

export interface ShareLink {
  id: string;
  project_id: string;
  expires_at: string;
  revoked_at: string | null;
  created_at: string;
}

export interface ResponseRow {
  id: string;
  org_id: string;
  project_id: string;
  project_code: string;
  mode: "kiosk" | "interviewer";
  interviewer_id: string | null;
  interviewer_code: string | null;
  kiosk_id: string | null;
  kiosk_label: string | null;
  started_at: string;
  finished_at: string;
  duration_seconds: number;
  local_hour: number;
  latitude: number | null;
  longitude: number | null;
  accuracy_m: number | null;
  auto_flags: string[];
  answers: Record<string, unknown>;
  status: ResponseStatus;
  status_reason: string | null;
  created_at: string;
}

export interface TrailRow {
  id: number;
  response_id: string;
  status: ResponseStatus;
  reason: string | null;
  created_by: string | null;
  created_at: string;
}

export type ProjectResults =
  | { project: string; code: string; suppressed: true; min_group_size: number }
  | {
      project: string;
      code: string;
      suppressed: false;
      n: number;
      min_group_size: number;
      items: Array<{ id: string; text: string; type: string; n: number; mean?: number; options?: Array<{ value: string; count: number }> }>;
    };

export function optionValue(o: string | { value: string; label?: string }): string {
  return typeof o === "string" ? o : o.value;
}
export function optionLabel(o: string | { value: string; label?: string }): string {
  return typeof o === "string" ? o : o.label ?? o.value;
}

export function fmtDuration(s: number | null | undefined): string {
  if (s === null || s === undefined) return "-";
  const n = Math.round(Number(s));
  const m = Math.floor(n / 60);
  const r = n % 60;
  return m > 0 ? `${m} min ${String(r).padStart(2, "0")} s` : `${r} s`;
}

declare module "@vixio/data" {
  interface RpcRegistry {
    "surveys.create_project": { args: { p_org: string; p_code: string; p_title: string; p_description?: string | null; p_client_name?: string | null; p_client_ref?: string | null }; returns: string };
    "surveys.update_project": {
      args: {
        p_project: string;
        p_title?: string | null;
        p_description?: string | null;
        p_client_name?: string | null;
        p_timezone?: string | null;
        p_min_duration_seconds?: number | null;
        p_max_duration_seconds?: number | null;
        p_allowed_hour_start?: number | null;
        p_allowed_hour_end?: number | null;
        p_require_geo?: boolean | null;
        p_straightline_min_items?: number | null;
        p_min_group_size?: number | null;
      };
      returns: null;
    };
    "surveys.set_questionnaire": { args: { p_project: string; p_form_slug: string }; returns: null };
    "surveys.set_sample_plan": { args: { p_project: string; p_method: SampleMethod; p_params: Record<string, unknown>; p_sample_size?: number | null; p_note?: string | null }; returns: string };
    "surveys.set_quota": { args: { p_project: string; p_item_id: string; p_option_value: string; p_label: string; p_target: number }; returns: string };
    "surveys.start_fieldwork": { args: { p_project: string }; returns: null };
    "surveys.close_fieldwork": { args: { p_project: string }; returns: { collected: number; approved: number; rejected: number; flagged: number; pending: number; sample_size: number } };
    "surveys.apply_interviewer": {
      args: { p_org_slug: string; p_name: string; p_email: string; p_phone: string; p_city?: string | null; p_availability?: string | null; p_experience?: string | null; p_privacy_ack?: boolean; p_context?: Record<string, unknown> | null };
      returns: { ok: boolean; message: string };
    };
    "surveys.review_interviewer": { args: { p_interviewer: string; p_decision: "approved" | "rejected" | "inactive"; p_note?: string | null; p_user?: string | null }; returns: string | null };
    "surveys.assign_interviewer": { args: { p_project: string; p_interviewer: string; p_target?: number; p_area?: string | null; p_quota_filter?: Record<string, unknown>; p_active?: boolean }; returns: string };
    "surveys.create_kiosk": { args: { p_project: string; p_label: string }; returns: { kiosk_id: string; token: string } };
    "surveys.revoke_kiosk": { args: { p_kiosk: string }; returns: null };
    "surveys.flag_response": { args: { p_response: string; p_reason: string }; returns: string };
    "surveys.approve_response": { args: { p_response: string; p_note?: string | null }; returns: string };
    "surveys.reject_response": { args: { p_response: string; p_reason: string }; returns: string };
    "surveys.create_share_link": { args: { p_project: string; p_days?: number }; returns: { link_id: string; token: string; expires_at: string } };
    "surveys.revoke_share_link": { args: { p_link: string }; returns: null };
    "surveys.project_results": { args: { p_project: string }; returns: ProjectResults };
    "surveys.get_shared_results": { args: { p_token: string }; returns: ProjectResults };
    "surveys.export_rows": { args: { p_project: string; p_statuses?: string[] }; returns: Array<{ line_no: number; cells: Array<string | null> }> };
    "surveys.sample_size_proportion": { args: { p_population: number | null; p_confidence: number; p_margin_pct: number; p_proportion_pct?: number }; returns: number };
  }
}

/** Rótulos pt-BR das ações de auditoria do módulo (AuditTimeline). */
registerAuditLabels({
  "surveys.project_created": "Projeto criado",
  "surveys.project_updated": "Regras do projeto alteradas",
  "surveys.questionnaire_set": "Questionário definido",
  "surveys.sample_plan_set": "Plano amostral salvo",
  "surveys.quota_set": "Cota salva",
  "surveys.fieldwork_started": "Campo iniciado",
  "surveys.fieldwork_closed": "Campo encerrado",
  "surveys.interviewer_applied": "Candidatura recebida",
  "surveys.interviewer_reviewed": "Entrevistador avaliado",
  "surveys.interviewer_assigned": "Entrevistador atribuído",
  "surveys.kiosk_created": "Quiosque criado",
  "surveys.kiosk_revoked": "Quiosque desativado",
  "surveys.response_collected": "Resposta coletada",
  "surveys.response_approved": "Resposta aprovada",
  "surveys.response_flagged": "Resposta sinalizada",
  "surveys.response_rejected": "Resposta descartada",
  "surveys.share_link_created": "Link de resultado criado",
  "surveys.share_link_revoked": "Link de resultado revogado",
  "surveys.exported": "Respostas exportadas",
});
